import { DISTRICT_FX, DISTRICTS_PER_ERA, ERA_DELVES, ERAS, FORGE_LEVEL_ERA, upToEra, NIGHTMARES, STRUCTURES, type NightmareType, GREAT_WORK_KEEP_FRAIL_BP, GROVE, HALL, SANCTUM, MAX_DISTRICTS, LAND_ERAS, LAND_LEVELS, daysMs, eraTowerCap, res, type DistrictKind, type Resources, type StructureType } from '@somnhold/content';
import { hexKey, hexToWorld } from '../hex.ts';
import { idiv, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { districtAt, mapEra, withDistrict, type DistrictDef } from '../walls.ts';

export { mapEra };
import { addRes, ensureRampart, event, scaleRes, sortedCellKeys } from './core.ts';
import type { CampaignState, Job, Ms, PlayerState } from './types.ts';

/** The Great Work stands on the keep's cell, like any other job. */
export const GREAT_WORK_KEY = '0,0';

/** The era the bastion is in. */
export const eraOf = (s: CampaignState): number => mapEra(s.map);

/**
 * A table for an incursion or a crypt with the era's creatures added (ERA_DELVES); `flyersOnly` keeps just
 * the ones that fly (the glass bridge).
 */
export const withDelves = (s: CampaignState, table: Partial<Record<NightmareType, number>>, flyersOnly = false): Partial<Record<NightmareType, number>> => {
  const add = upToEra(ERA_DELVES, eraOf(s));
  if (!add) return table;
  const out = { ...table };
  for (const k of Object.keys(add).sort() as NightmareType[]) if (!flyersOnly || NIGHTMARES[k].flying) out[k] = (out[k] ?? 0) + (add[k] ?? 0);
  return out;
};

/** The Great Work under way, if any. */
export const greatWork = (s: CampaignState): Job | null => {
  const j = s.cells[GREAT_WORK_KEY]?.job;
  return j && j.kind === 'district' ? j : null;
};

/**
 * What the next Great Work asks: the era table row, or null when the bastion can't grow any more. Eras II and III
 * raise districts (two each, the first opening the era); the later ones crown the keep (`crown`).
 */
export const nextDistrict = (s: CampaignState): { index: number; era: number; level: number; buildMs: number; crown: boolean } | null => {
  const index = s.map.districts?.length ?? 0;
  // Terraced land has no districts: every Great Work crowns the keep (docs/CONSTRUCCION.md).
  if (index < MAX_DISTRICTS && !s.map.land) {
    const row = ERAS[idiv(index, DISTRICTS_PER_ERA)];
    if (!row) return null;
    return { index, era: 2 + idiv(index, DISTRICTS_PER_ERA), level: row.level, buildMs: row.buildMs, crown: false };
  }
  const era = eraOf(s) + 1;
  const row = ERAS[era - 2];
  if (!row) return null;
  return { index, era, level: row.level, buildMs: row.buildMs, crown: true };
};

/** A crowning Great Work is done: the bastion steps into its next era. */
export const crownKeep = (s: CampaignState, now: Ms, pid = ''): void => {
  s.map = { ...s.map, crowns: (s.map.crowns ?? 0) + 1 };
  event(s, now, 'district', { pid, data: { crown: true, era: eraOf(s) } });
};

/** Heroes that count for the level gate: in the campaign and played in the last four days. */
export const activeHeroes = (s: CampaignState, now: Ms): PlayerState[] => s.players.filter((p) => !p.left && now - p.lastActiveAt <= daysMs(4));

/**
 * The group's level (docs/PROGRESION.md): the average level of the active heroes, rounded down; of everyone
 * still in the campaign when nobody played lately. Each player counts with their best hero, played or benched. It opens the eras (and, later, the saga).
 */
export const groupLevel = (s: CampaignState, now: Ms): number => {
  const active = activeHeroes(s, now);
  const ps = active.length > 0 ? active : s.players.filter((p) => !p.left);
  if (ps.length === 0) return 1;
  let sum = 0;
  // Each player's best hero: a new one on the bench or in play doesn't hold the group back.
  for (const p of ps) {
    let best = p.hero.level;
    for (const b of p.bench ?? []) best = imax(best, b.hero.level);
    sum += best;
  }
  return idiv(sum, ps.length);
};

/**
 * Whether the group has reached a level gate: the outings past the first ones open with the group level, like the
 * eras and the saga, never with the Marea (the Marea counts the calendar, not progress; DECISIONS 2026-11-02).
 */
export const groupReached = (s: CampaignState, minGroup: number): boolean => groupLevel(s, s.clock) >= minGroup;

/** Whether the group is still under the level the next district asks. */
export const groupBelow = (s: CampaignState, now: Ms): boolean => {
  const next = nextDistrict(s);
  return !!next && groupLevel(s, now) < next.level;
};

/**
 * The highest level a structure can be raised to in this era: towers 4 from era II and 5 from era IV, the forge by
 * FORGE_LEVEL_ERA, the rest 3. On terraced land towers and traps follow LAND_ERAS (towers 6 in era V, traps 4 and 5).
 */
export const levelTop = (s: CampaignState, structure: StructureType): number => {
  const era = eraOf(s);
  const cat = STRUCTURES[structure].category;
  if (s.map.land && (cat === 'tower' || cat === 'trap')) {
    const top = cat === 'tower' ? LAND_ERAS.towerTop : LAND_ERAS.trapTop;
    return top[imin(era, top.length) - 1] ?? 3;
  }
  if (cat === 'tower') return era >= 4 ? 5 : era >= 2 ? 4 : 3;
  if (structure === 'forge') {
    let top = 3;
    for (const [lv, need] of Object.entries(FORGE_LEVEL_ERA)) if (era >= need && Number(lv) > top) top = Number(lv);
    return top;
  }
  return 3;
};

/** Whether a structure's next level waits for a later era (towers and the forge; traps too on terraced land). */
export const levelEraLocked = (s: CampaignState, structure: StructureType, level: number): boolean => {
  const cat = STRUCTURES[structure].category;
  if (s.map.land && (cat === 'tower' || cat === 'trap')) {
    const top = cat === 'tower' ? LAND_ERAS.towerTop : LAND_ERAS.trapTop;
    return level < (top[top.length - 1] ?? 3);
  }
  return (cat === 'tower' && (level === 3 || level === 4)) || (structure === 'forge' && FORGE_LEVEL_ERA[level + 1] !== undefined);
};

/** The era a structure is first built in: its own, or earlier on terraced land (LAND_ERAS.minEra). */
export const minEraOf = (s: CampaignState, type: StructureType): number =>
  (s.map.land ? LAND_ERAS.minEra[type] : undefined) ?? STRUCTURES[type].minEra ?? 1;

/**
 * A seasoned artificer in the group who played lately (in play or on the bench): on terraced land, levels 3 and up
 * cost more without one. A fresh one made just for the order doesn't count (LAND_ERAS.artificerLevel).
 */
export const activeArtificer = (s: CampaignState): boolean =>
  s.players.some(
    (p) =>
      !p.left &&
      s.clock - p.lastActiveAt <= daysMs(LAND_ERAS.artificerDays) &&
      [p.hero, ...(p.bench ?? []).map((b) => b.hero)].some((h) => h.heroClass === 'artificer' && h.level >= LAND_ERAS.artificerLevel),
  );

/** The next Great Work's cost: per player in the campaign. */
export const greatWorkCost = (s: CampaignState): Resources => {
  const next = nextDistrict(s);
  if (!next) return res({});
  const row = ERAS[next.era - 2]!;
  const n = s.players.filter((p) => !p.left).length;
  return res({ wood: row.costPerPlayer.wood * n, stone: row.costPerPlayer.stone * n, crystal: row.costPerPlayer.crystal * n });
};

/** Sides a district can go on right now. */
export const freeSides = (s: CampaignState): number[] => {
  const out: number[] = [];
  for (let side = 0; side < 6; side++) if (withDistrict(s.map, { side, kind: 'military' })) out.push(side);
  return out;
};

/**
 * Raises a district: the walls move out around it, the old ring between them comes down, entrances it
 * swallowed move out to its wall (a gate built there goes with them), and new wall runs stand on the
 * rest of its edge.
 */
export const raiseDistrict = (s: CampaignState, d: DistrictDef, now: Ms, pid = ''): boolean => {
  const before = s.map;
  const next = withDistrict(before, d);
  if (!next) return false;
  s.map = next;
  // Gates follow their entrance out.
  before.sectors.forEach((sec, i) => {
    const to = next.sectors[i];
    if (!to || (to.gate.q === sec.gate.q && to.gate.r === sec.gate.r)) return;
    const from = hexKey(sec.gate);
    const cell = s.cells[from];
    if (cell && (cell.built?.structure === 'gate' || cell.job?.structure === 'gate')) {
      s.cells[hexKey(to.gate)] = cell;
      delete s.cells[from];
    }
  });
  // The old ring inside the new walls comes down.
  for (const key of sortedCellKeys(s)) {
    const b = s.cells[key]?.built;
    if (b?.structure !== 'rampart') continue;
    const [q, r] = key.split(',').map(Number) as [number, number];
    if (districtAt(next, q, r)) delete s.cells[key];
  }
  ensureRampart(s);
  event(s, now, 'district', { pid, data: { side: d.side, kind: d.kind, era: eraOf(s) } });
  return true;
};

/** Military districts: what's built or improved inside costs less. */
export const costAt = (s: CampaignState, q: number, r: number, cost: Resources): Resources =>
  districtAt(s.map, q, r)?.kind === 'military' ? scaleRes(cost, DISTRICT_FX.military.costBp) : cost;

/** One more golem per guild district; on terraced land, one more per era past the first (docs/CONSTRUCCION.md). */
export const districtGolems = (s: CampaignState): number =>
  s.map.land ? eraOf(s) - 1 : (s.map.districts ?? []).filter((d) => d.kind === 'guild').length * DISTRICT_FX.guild.golems;

/** After every wave the grove yields wood (twice with a druid in the group); returns how much. */
export const groveAfterWave = (s: CampaignState): number => {
  if (!standing(s, 'grove')) return 0;
  const wood = GROVE.woodPerWave[eraOf(s)] ?? GROVE.woodPerWave[GROVE.woodPerWave.length - 1] ?? 0;
  const druid = s.players.some((p) => !p.left && p.hero.heroClass === 'druid');
  const lv = s.map.land ? mulBp(wood, LAND_LEVELS.grove.woodBp[bestLevel(s, 'grove') - 1] ?? 10000) : wood;
  const give = druid ? mulBp(lv, GROVE.druidBp) : lv;
  addRes(s.storage, { wood: give });
  return give;
};

/**
 * After every wave the cleric's sanctum mends what stands within its reach (more with a cleric in the group):
 * a share of each one's HP, never past its maximum and never a ruin. Returns the HP it gave back.
 */
export const sanctumAfterWave = (s: CampaignState, maxHpOf: (key: string) => number): number => {
  const keys = sortedCellKeys(s);
  let at: { x: number; y: number } | null = null;
  for (const key of keys) {
    const b = s.cells[key]?.built;
    if (b?.structure !== 'sanctum' || b.hp <= 0) continue;
    const [q, r] = key.split(',').map(Number) as [number, number];
    at = hexToWorld(q, r);
    break;
  }
  if (!at) return 0;
  const cleric = s.players.some((p) => !p.left && p.hero.heroClass === 'cleric');
  const base = cleric ? SANCTUM.clericHealBp : SANCTUM.healBp;
  const bp = s.map.land ? mulBp(base, LAND_LEVELS.sanctum.healBp[bestLevel(s, 'sanctum') - 1] ?? 10000) : base;
  let mended = 0;
  for (const key of keys) {
    const b = s.cells[key]?.built;
    if (!b || b.hp <= 0) continue;
    const [q, r] = key.split(',').map(Number) as [number, number];
    const c = hexToWorld(q, r);
    if (!withinRange(c.x, c.y, at.x, at.y, STRUCTURES.sanctum.range)) continue;
    const max = maxHpOf(key);
    if (max <= 0 || b.hp >= max) continue;
    const hp = imin(max, b.hp + mulBp(max, bp));
    mended += hp - b.hp;
    b.hp = hp;
  }
  return mended;
};

/** The clan hall's trophies counting for this Marea's waves (bp of damage for every hero), if it stands. */
export const trophyBp = (s: CampaignState, marea: number): number =>
  standing(s, 'hall') && s.trophies?.marea === marea ? s.trophies.n * HALL.trophyBp : 0;

/** After a wave: each warlord or boss felled hangs a trophy in the clan hall (this Marea, up to the cap). */
export const hangTrophies = (s: CampaignState, marea: number, felled: number): void => {
  if (felled <= 0 || !standing(s, 'hall')) return;
  const n = s.trophies?.marea === marea ? s.trophies.n : 0;
  const max = s.map.land ? (LAND_LEVELS.hall.maxTrophies[bestLevel(s, 'hall') - 1] ?? HALL.maxTrophies) : HALL.maxTrophies;
  s.trophies = { marea, n: imin(max, n + felled) };
};

/** After every wave: quarries yield (returns what they gave, or null without one), sacred ground mends what stands on it. */
export const districtsAfterWave = (s: CampaignState, maxHpOf: (key: string) => number): { wood: number; stone: number } | null => {
  const n = s.players.filter((p) => !p.left).length;
  const quarries = (s.map.districts ?? []).filter((d) => d.kind === 'quarry').length;
  const quarry = quarries > 0 ? { wood: DISTRICT_FX.quarry.perPlayer.wood * n * quarries, stone: DISTRICT_FX.quarry.perPlayer.stone * n * quarries } : null;
  if (quarry) addRes(s.storage, quarry);
  if (!(s.map.districts ?? []).some((d) => d.kind === 'sacred')) return quarry;
  for (const key of sortedCellKeys(s)) {
    const b = s.cells[key]?.built;
    if (!b || b.hp <= 0) continue;
    const [q, r] = key.split(',').map(Number) as [number, number];
    if (districtAt(s.map, q, r)?.kind !== 'sacred') continue;
    const max = maxHpOf(key);
    if (max <= 0) continue;
    b.hp += mulBp(max, DISTRICT_FX.sacred.healBp);
    if (b.hp > max) b.hp = max;
  }
  return quarry;
};

/** Whether one of these stands (not in ruins) in the bastion. */
export const standing = (s: CampaignState, type: StructureType): boolean => {
  for (const key of sortedCellKeys(s)) {
    const b = s.cells[key]?.built;
    if (b?.structure === type && b.hp > 0) return true;
  }
  return false;
};

/** The best level among the standing structures of a type (0: none standing). */
export const bestLevel = (s: CampaignState, type: StructureType): number => {
  let best = 0;
  for (const key of sortedCellKeys(s)) {
    const b = s.cells[key]?.built;
    if (b?.structure === type && b.hp > 0 && b.level > best) best = b.level;
  }
  return best;
};

/** How many of a structure stand or are planned (ruins count: they still take the spot). */
export const countOf = (s: CampaignState, type: StructureType): number => {
  let n = 0;
  for (const key of sortedCellKeys(s)) {
    const c = s.cells[key];
    if (c?.built?.structure === type || (c?.job?.kind === 'new' && c.job.structure === type)) n++;
    // A tower mounted on a wall run (the adarve), or on its way up, counts as one of its kind.
    else if (c?.built?.mount?.type === type || (c?.job?.kind === 'mount' && c.job.mount === type)) n++;
  }
  return n;
};

/** The era's cap for a tower and how many there are, or null when it has no cap. */
export const towerCap = (s: CampaignState, type: StructureType): { n: number; cap: number } | null => {
  const era = eraOf(s);
  let cap = (s.map.land ? LAND_ERAS.capAt[type]?.[era] : undefined) ?? eraTowerCap(type, era, s.players.filter((p) => !p.left).length);
  // On terraced land a workshop raised higher makes room for one more clockwork turret.
  if (cap !== null && s.map.land && type === 'clockwork') cap += LAND_LEVELS.workshop.clockworkAdd[bestLevel(s, 'workshop') - 1] ?? 0;
  return cap === null ? null : { n: countOf(s, type), cap };
};

/** While the Great Work is unfinished the keep is frailer. */
export const keepFrailBp = (s: CampaignState): number => (greatWork(s) ? GREAT_WORK_KEEP_FRAIL_BP : 10000);

export type { DistrictKind };
